import React,{useEffect,useRef,useState} from 'react';
import {useAuth} from '../services/authContext';
import {fetchPhotoBlob,isProtectedPhotoUrl} from '../services/photoMedia';
import {getPhotoDisplayUrl} from '../services/scoutingImage';
import type {ScoutingPhoto} from '../types';

export default function PhotoNewTab({photo,children,className}:{photo:ScoutingPhoto;children:React.ReactNode;className?:string}){
 const {user,privileges,idToken}=useAuth();
 const metadata=photo as ScoutingPhoto&{original_download_url?:string;farm_id?:string;audience?:string;publication_revision?:number;cached?:boolean;pending?:boolean};
 const display=getPhotoDisplayUrl(photo),original=privileges.scouting?.view?metadata.original_download_url:undefined,url=original||display;
 const nature=metadata.audience==='nature'||display.includes('audience=nature');
 const allowed=user?.status==='Active'&&(nature?privileges.communityParticipation?.view:privileges.scouting?.view)&&(!metadata.farm_id||user.roles?.includes('Admin')||user.farm_ids?.includes(metadata.farm_id));
 const scope=JSON.stringify([photo.photo_id,url,user?.email,user?.status,user?.roles,user?.farm_ids,privileges,idToken,metadata.publication_revision,metadata.cached]);
 const current=useRef(scope);current.current=scope;
 const pending=useRef<{abort:AbortController;tab:Window}|null>(null);
 const [busy,setBusy]=useState(false),[failure,setFailure]=useState<{scope:string;message:string}|null>(null);
 const error=failure?.scope===scope?failure.message:'';
 useEffect(()=>{setBusy(false);setFailure(null);return()=>{pending.current?.abort.abort();pending.current?.tab.close();pending.current=null;};},[scope]);
 const open=async()=>{
  if(!allowed||busy)return;
  // Open synchronously from the click so an authenticated fetch does not turn
  // this into a blocked popup. Never put a token or private storage URL in it.
  const tab=window.open('about:blank','_blank');
  if(!tab){setFailure({scope,message:'Allow this site to open a photo tab, then try again.'});return;}
  tab.opener=null;tab.document.title='Photo';
  const loading=tab.document.createElement('p');loading.textContent='Opening photo…';tab.document.body.appendChild(loading);
  const abort=new AbortController();pending.current={abort,tab};setBusy(true);setFailure(null);
  try{
   const revision=String(metadata.publication_revision||photo.server_version||'');
   let blob:Blob;
   if(isProtectedPhotoUrl(url))blob=await fetchPhotoBlob(url,abort.signal,revision,original?false:!!metadata.cached);
   else if(metadata.pending&&/^(blob:|data:image\/(png|jpeg|webp);base64,)/.test(url))blob=await fetch(url,{signal:abort.signal}).then(response=>response.blob());
   else throw new Error('This photo cannot be opened. Refresh and try again.');
   if(abort.signal.aborted||current.current!==scope||tab.closed)return;
   const objectUrl=URL.createObjectURL(blob);
   try{tab.location.replace(objectUrl);}catch(e){URL.revokeObjectURL(objectUrl);throw e;}
   // The browser already has the permitted file after navigation. Release this
   // page's temporary URL; an open photo tab is the user's retained file view.
   setTimeout(()=>URL.revokeObjectURL(objectUrl),60000);
   pending.current=null;
  }catch(e){tab.close();if(current.current===scope&&!abort.signal.aborted)setFailure({scope,message:(e as Error).message});}
  finally{if(pending.current?.abort===abort)pending.current=null;if(current.current===scope)setBusy(false);}
 };
 return <div className={className}>
  <button type="button" aria-label={original?'Open original photo in a new tab':'Open photo in a new tab'} aria-busy={busy} disabled={busy||!allowed} onClick={()=>void open()} className="block w-full cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-usda-600 focus-visible:outline-offset-[-3px]">{children}</button>
  {error&&<p role="alert" className="text-sm text-red-700 bg-white p-3">{error}</p>}
 </div>;
}
